import React from "react";
import { Menu } from "antd";
import SubMenu from "antd/lib/menu/SubMenu";
import {
  DatabaseOutlined,
  ExperimentOutlined,
  FileOutlined,
  TeamOutlined,
  SendOutlined,
  FormOutlined,
  ToolOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import languageKeys from "../translations";
import { useUser } from "../../../contexts/user-context";
import { userCan } from "../../../utils/accessContext";

const AdminMenu = ({ selectedKeys, links, translate, role }) => {
  const { user } = useUser();
  const canRead = (moduleName) => userCan(user, moduleName, "read");
  const canCreateInvites = userCan(user, "invitations", "create");
  const isAssistant =
    role === "assistant" || (user && user.role && user.role.role === "ASSISTANT");
  const isFullAdmin = !isAssistant;
  const showUsers = isFullAdmin || canRead("users") || canRead("companies");
  const showContent =
    isFullAdmin ||
    canRead("report_templates") ||
    canRead("email_templates") ||
    canRead("custom_texts") ||
    canRead("notification_templates");
  const showDatabase = isFullAdmin || canRead("database") || canRead("gallery");
  const showAlgorithm = isFullAdmin || canRead("main_algorithm");
  const showAdvanced = isFullAdmin || canRead("advanced");
  const showConfig =
    showContent ||
    showDatabase ||
    showAlgorithm ||
    showAdvanced ||
    canRead("editable_values") ||
    canRead("mobile_apps");
  const key = (selectedKeys || [])[0];
  const openKeys =
    key &&
    key
      .split(".")
      .slice(0, -1)
      .reduce((keys, _, i, arr) => {
        keys.push(arr.slice(0, i + 1).join("."));
        return keys;
      }, []);

  // Keep original leaf keys so existing pages still highlight correctly.
  // Configuration (A6) wraps Content, Settings, Database, Algorithm, Advanced.
  const configPrefixes = [
    "A4.1",
    "A4.3",
    "A4.3.5",
    "A4.4",
    "A4.5.1",
    "A4.5.2",
    "A4.6",
    "A4.7",
    "A4.9",
    "A4.10",
    "A4.11",
    "A5",
  ];
  const needsConfigOpen =
    key &&
    configPrefixes.some((prefix) => key === prefix || key.startsWith(`${prefix}.`));

  const advancedPrefixes = ["A4.5.2", "A4.6", "A4.7", "A4.10"];
  const needsAdvancedOpen =
    key && advancedPrefixes.some((prefix) => key === prefix || key.startsWith(`${prefix}.`));
  const needsAlgorithmOpen = key === "A4.5.1" || (key && key.startsWith("A4.4"));
  const needsContentOpen = key && (key === "A4.11" || key.startsWith("A4.11."));
  const needsDatabaseOpen = key && (key === "A4.3" || key.startsWith("A4.3."));
  const needsTextConfigOpen = key && (key === "A4.3.5" || key.startsWith("A4.3.5."));

  const effectiveOpenKeys = [
    ...(openKeys || []),
    ...(needsConfigOpen ? ["A6"] : []),
    ...(needsContentOpen ? ["A4.11"] : []),
    ...(needsDatabaseOpen ? ["A4.3"] : []),
    ...(needsTextConfigOpen ? ["A4.3.5"] : []),
    ...(needsAlgorithmOpen ? ["A4.4"] : []),
    ...(needsAdvancedOpen ? ["A5"] : []),
    ...(key && (key === "A4.5.2" || key.startsWith("A4.5.2.")) ? ["A4.5.2"] : []),
    ...(key && (key === "A4.7" || key.startsWith("A4.7.")) ? ["A4.7"] : []),
  ].filter((value, index, arr) => arr.indexOf(value) === index);

  const subMenuTitleStyle = { paddingLeft: 0, fontSize: 11 };
  const titleSpanStyle = { paddingLeft: 0, fontSize: 13 };

  return (
    <Menu
      key={"Menu"}
      style={{
        background: "transparent",
        height: "calc(100% - 94px)",
        overflowX: "hidden",
        overflowY: "auto",
      }}
      inlineIndent={12}
      theme="light"
      mode="inline"
      selectedKeys={selectedKeys || []}
      defaultOpenKeys={effectiveOpenKeys}
    >
      <Menu.Item key="A0">{links.welcome}</Menu.Item>
      {isFullAdmin && (
        <Menu.Item key="A0.1">{links.prescriberDashboard}</Menu.Item>
      )}
      {(isFullAdmin || canRead("invitations")) && (
        <Menu.Item key="A1">{links.history}</Menu.Item>
      )}
      {(isFullAdmin || canCreateInvites) && (
        <Menu.Item key="A2">{links.answerQuestionnaire}</Menu.Item>
      )}
      {(isFullAdmin || canRead("invitations")) && (
        <Menu.Item key="A3">{links.invites}</Menu.Item>
      )}

      {showUsers && (
      <SubMenu
        style={subMenuTitleStyle}
        key="A4.2"
        title={
          <span style={titleSpanStyle}>
            <TeamOutlined />
            <span>{translate(languageKeys.usersMenu)}</span>
          </span>
        }
      >
        {(isFullAdmin || canRead("users")) && (
          <Menu.Item key="A4.2.1">{links.usersBackoffice}</Menu.Item>
        )}
        {(isFullAdmin || canRead("companies")) && (
          <Menu.Item key="A4.2.5">{links.companies}</Menu.Item>
        )}
        {(isFullAdmin || canRead("users")) && (
          <Menu.Item key="A4.2.4">{links.prescribers}</Menu.Item>
        )}
        {(isFullAdmin || canRead("users")) && (
          <Menu.Item key="A4.2.3">{links.pacients}</Menu.Item>
        )}
        {(isFullAdmin || canRead("users")) && (
          <Menu.Item key="A4.2.2">{links.usersApp}</Menu.Item>
        )}
      </SubMenu>
      )}

      {showConfig && (
      <SubMenu
        style={subMenuTitleStyle}
        key="A6"
        title={
          <span style={titleSpanStyle}>
            <SettingOutlined />
            <span>{translate(languageKeys.configurationMenu)}</span>
          </span>
        }
      >
        {showContent && (
        <SubMenu
          style={subMenuTitleStyle}
          key="A4.11"
          title={
            <span style={titleSpanStyle}>
              <FormOutlined />
              <span>{translate(languageKeys.contentManagement)}</span>
            </span>
          }
        >
          {(isFullAdmin || canRead("report_templates")) && (
            <Menu.Item key="A4.11.3">{links.reports}</Menu.Item>
          )}
          {(isFullAdmin || canRead("email_templates")) && (
            <Menu.Item key="A4.11.1">{links.emailTemplates}</Menu.Item>
          )}
          {(isFullAdmin || canRead("custom_texts")) && (
            <Menu.Item key="A4.11.2">{links.customTexts}</Menu.Item>
          )}
          {(isFullAdmin || canRead("notification_templates")) && (
            <Menu.Item key="A4.11.4">{links.pushNotifications}</Menu.Item>
          )}
        </SubMenu>
        )}

        {(isFullAdmin || canRead("editable_values")) && (
          <Menu.Item key="A4.1">{links.backofficeValues}</Menu.Item>
        )}

        {showDatabase && (
        <SubMenu
          style={subMenuTitleStyle}
          key="A4.3"
          title={
            <span style={titleSpanStyle}>
              <DatabaseOutlined />
              <span>{translate(languageKeys.data_base)}</span>
            </span>
          }
        >
          {(isFullAdmin || canRead("database")) && (
            <Menu.Item key="A4.3.1">{links.questionnaires}</Menu.Item>
          )}
          {(isFullAdmin || canRead("database")) && (
            <Menu.Item key="A4.3.2">{links.questions}</Menu.Item>
          )}
          {(isFullAdmin || canRead("database")) && (
            <Menu.Item key="A4.3.3">{links.answersOptions}</Menu.Item>
          )}
          {(isFullAdmin || canRead("gallery")) && (
            <Menu.Item key="A4.3.4">{links.gallery}</Menu.Item>
          )}
          {(isFullAdmin || canRead("database")) && (
            <SubMenu
              style={subMenuTitleStyle}
              key="A4.3.5"
              title={
                <span style={titleSpanStyle}>
                  <span>{translate(languageKeys.textConfigMenu)}</span>
                </span>
              }
            >
              <Menu.Item key="A4.3.5.1">{links.textConfigCriteria}</Menu.Item>
              <Menu.Item key="A4.3.5.2">{links.textConfigCriteriaUsage}</Menu.Item>
              <Menu.Item key="A4.3.5.3">{links.textConfigMetaSkill}</Menu.Item>
              <Menu.Item key="A4.3.5.4">{links.textConfigPrinciple}</Menu.Item>
              <Menu.Item key="A4.3.5.5">{links.textConfigElement}</Menu.Item>
              <Menu.Item key="A4.3.5.6">{links.textConfigFeeling}</Menu.Item>
              <Menu.Item key="A4.3.5.7">{links.textConfigOrgan}</Menu.Item>
              <Menu.Item key="A4.3.5.8">{links.textConfigSeason}</Menu.Item>
              <Menu.Item key="A4.3.5.9">{links.textConfigNaturalElement}</Menu.Item>
              <Menu.Item key="A4.3.5.10">{links.textConfigCharacter}</Menu.Item>
            </SubMenu>
          )}
        </SubMenu>
        )}

        {showAlgorithm && (
        <SubMenu
          style={subMenuTitleStyle}
          key="A4.4"
          title={
            <span style={titleSpanStyle}>
              <ExperimentOutlined />
              <span>{translate(languageKeys.algorithmMenu)}</span>
            </span>
          }
        >
          <Menu.Item key="A4.4.1">{links.criteria}</Menu.Item>
          <Menu.Item key="A4.4.2">{links.criteriaUsage}</Menu.Item>
          <Menu.Item key="A4.4.3">{links.metaSkill}</Menu.Item>
          <Menu.Item key="A4.4.4">{links.principle}</Menu.Item>
          <Menu.Item key="A4.4.5">{links.algorithmConfig}</Menu.Item>
          <Menu.Item key="A4.5.1">{links.element}</Menu.Item>
        </SubMenu>
        )}

        {(isFullAdmin || canRead("mobile_apps")) && (
          <Menu.Item key="A4.9">{links.app}</Menu.Item>
        )}

        {showAdvanced && (
        <SubMenu
          style={{ ...subMenuTitleStyle, opacity: 0.85 }}
          key="A5"
          title={
            <span style={titleSpanStyle}>
              <ToolOutlined />
              <span>{translate(languageKeys.advancedMenu)}</span>
            </span>
          }
        >
          <SubMenu
            style={subMenuTitleStyle}
            key="A4.5.2"
            title={
              <span style={titleSpanStyle}>
                <FileOutlined />
                <span>{translate(languageKeys.attributesMenu)}</span>
              </span>
            }
          >
            <Menu.Item key="A4.5.2.1">{links.feeling}</Menu.Item>
            <Menu.Item key="A4.5.2.2">{links.organ}</Menu.Item>
            <Menu.Item key="A4.5.2.3">{links.season}</Menu.Item>
            <Menu.Item key="A4.5.2.4">{links.naturalElement}</Menu.Item>
            <Menu.Item key="A4.5.2.5">{links.character}</Menu.Item>
          </SubMenu>
          <Menu.Item key="A4.6">{links.input}</Menu.Item>
          <SubMenu
            style={subMenuTitleStyle}
            key="A4.7"
            title={
              <span style={titleSpanStyle}>
                <SendOutlined />
                <span>{translate(languageKeys.outputMenu)}</span>
              </span>
            }
          >
            <Menu.Item key="A4.7.1">{links.algorithmOutput}</Menu.Item>
            <Menu.Item key="A4.7.2">{links.generalOutput}</Menu.Item>
          </SubMenu>
          <Menu.Item key="A4.10">{links.criteriaManagement}</Menu.Item>
        </SubMenu>
        )}
      </SubMenu>
      )}
    </Menu>
  );
};

export default AdminMenu;
